import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProtectedRoute from '../ProtectedRoute/ProtectedRoute';
import Sidebar from '../Sidebar/Sidebar';
import MobileTopBar from '../MobileTopBar/MobileTopBar';
import { ErrorBoundary } from '../ErrorBoundary';

interface ProtectedLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_ID = 'app-sidebar';

// The shell lives inside ProtectedRoute so that when the gate swaps in the
// terms page or the loading spinner, the drawer unmounts and its effect
// cleanup drops the body class and the Escape listener.
const AppShell: React.FC<ProtectedLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = useCallback(() => setIsMenuOpen((open) => !open), []);

  // A deliberate close (close button, scrim, Escape) hands focus back to the
  // menu button so keyboard users are not dropped at the top of the page.
  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  // In-app navigation closes the drawer without moving focus. location.key,
  // not pathname, so tapping the current page's link closes it too.
  // External Training links open a new tab and leave the drawer as it is.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    // Scroll lock; App.css applies it below the breakpoint only.
    document.body.classList.add('sidebar-drawer-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('sidebar-drawer-open');
    };
  }, [isMenuOpen, closeMenu]);

  return (
    <div className="app-layout">
      <Sidebar
        id={SIDEBAR_ID}
        isOpen={isMenuOpen}
        onClose={closeMenu}
        closeButtonRef={closeButtonRef}
      />
      {isMenuOpen && (
        // Pointer dismiss only; keyboard and screen-reader users have the
        // close button and Escape, so the scrim stays out of the a11y tree.
        <div className="sidebar-scrim" aria-hidden="true" onClick={closeMenu} />
      )}
      <main className="main-content">
        <MobileTopBar
          isMenuOpen={isMenuOpen}
          onMenuClick={toggleMenu}
          menuId={SIDEBAR_ID}
          menuButtonRef={menuButtonRef}
        />
        {/* Every route renders its own ProtectedLayout, but React Router reuses
            the same instance across routes, so the ErrorBoundary would stay
            tripped after a crash. location.key changes on every navigation and
            resets it. */}
        <ErrorBoundary resetKey={location.key}>
          {children}
        </ErrorBoundary>
      </main>
    </div>
  );
};

const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ children }) => (
  <ProtectedRoute>
    <AppShell>{children}</AppShell>
  </ProtectedRoute>
);

export default ProtectedLayout;
